import { CreateCategoryFormValues } from '../components'
import { DownloadsItemDto } from '../dto'
import { DownloadFileItem, DownloadsCategoryModel, DownloadsFilter, DownloadsModel } from '../models/downloads.model'

/**
 * sort an array of DownloadsModel into files and categories
 */
export const getFilesAndCategories = (items: DownloadsModel[]): { files: DownloadFileItem[]; categories: DownloadsCategoryModel[] } => {
  return items.reduce(
    (result, item) => {
      if (item.type === 'file') {
        result.files.push(item)
      } else if (item.type === 'category') {
        result.categories.push(item as DownloadsCategoryModel)
      }
      return result
    },
    { files: [] as DownloadFileItem[], categories: [] as DownloadsCategoryModel[] }
  )
}

/**
 * transform an object of filters into a string that can be parsed by the backend
 */
export const transformFilters = (filters?: DownloadsFilter, searchString?: string): string | undefined => {
  if (!filters) return undefined

  const filterStrings = []
  if (filters.userLevel && filters.userLevel !== 'all') {
    if (filters.userLevel === 'e3dc_service') {
      filterStrings.push('visibleToInstaller=false,visibleToCustomer=false')
    } else if (filters.userLevel === 'installer_partner') {
      filterStrings.push('visibleToInstaller=true')
    } else {
      filterStrings.push('visibleToCustomer=true')
    }
  }

  if (filters.country && filters.country !== 'ALL') {
    filterStrings.push(`country=${filters.country}`)
  }

  if (filters.language && filters.language !== 'all') {
    filterStrings.push(`language=${filters.language}`)
  }

  if (filters.type) {
    filterStrings.push(`type=${filters.type}`)
  }

  if (filters.forbiddenId) {
    filterStrings.push(`id!=${filters.forbiddenId}`)
  }

  if (searchString && searchString !== '') {
    filterStrings.push(`fileName=*${searchString}*`)
  }

  if (filters.path) {
    filterStrings.push(`path=${filters.path}`)
  }

  return filterStrings.join(',')
}

/**
 * transform the category payload to match the backend structure
 */
export const transformCategoryPayload = (
  payload: Omit<CreateCategoryFormValues, 'parentId' | 'path'>
): Omit<CreateCategoryFormValues, 'parentId' | 'path' | 'nameDe' | 'nameEn' | 'nameFr' | 'nameNl' | 'nameXx'> & {
  categoryTranslationList: { language: string; name: string }[]
} => {
  const { nameDe, nameEn, nameFr, nameNl, nameXx, ...rest } = payload

  return {
    ...rest,
    categoryTranslationList: [
      { language: 'de', name: nameDe },
      { language: 'en', name: nameEn },
      { language: 'fr', name: nameFr },
      { language: 'nl', name: nameNl },
      { language: 'xx', name: nameXx || nameEn },
    ],
  }
}

/**
 * check if the parent category has higher visibility restrictions than the child category
 */
export const doesParentHaveHigherRestrictions = (parent: DownloadsItemDto, child: DownloadsItemDto): boolean => {
  if (parent.visibleToCustomer === false && child.visibleToCustomer === true) {
    return true
  }
  if (parent.visibleToInstaller === false && child.visibleToInstaller === true) {
    return true
  }
  return false
}

/**
 * Measure the width of a text string as it would be rendered in the document, before it is actually rendered.
 */
export const measureTextWidth = (text: string, fontSize?: string | number): number => {
  const span = document.createElement('span')
  span.textContent = text
  span.style.visibility = 'hidden'
  span.style.position = 'absolute'
  span.style.whiteSpace = 'nowrap'
  span.style.fontSize = typeof fontSize === 'number' ? `${fontSize}px` : fontSize || 'inherit'
  document.body.appendChild(span)
  const width = span.clientWidth
  span.remove()
  return width
}

export const splitFileNameAndExtenstion = (fileName: string): { name: string; extension: string } => {
  const nameArr = fileName.split('.')
  const extension = nameArr.pop()
  const nameWithoutExtension = nameArr.join('.')

  return {
    name: nameWithoutExtension,
    extension: extension ?? '',
  }
}
